import * as React from 'react';
import { useState } from 'react';
import {
  Move,
  PencilLine,
  Bath,
  Box,
  LayoutGrid,
  Compass,
  Crop
} from 'lucide-react';
import { ToolType, SymbolType, SystemType } from '@shared/types/tsk';
import TradeIcon from './TradeIcon';
import { SYSTEM_COLORS } from '../utils/drawing';

interface ToolbarProps {
  activeTool: ToolType;
  setActiveTool: (tool: ToolType) => void;
  activeSymbol: SymbolType;
  setActiveSymbol: (symbol: SymbolType) => void;
  activeSystem: SystemType;
  setActiveSystem: (system: SystemType) => void;
}

const Toolbar = ({
  activeTool, setActiveTool, activeSymbol, setActiveSymbol,
  activeSystem, setActiveSystem
}: ToolbarProps) => {
  const [activeCategory, setActiveCategory] = useState<'fixtures' | 'fittings' | 'direction'>('fixtures');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const fixtureSymbols = [
    { type: SymbolType.WC, label: 'WC' },
    { type: SymbolType.BASIN, label: 'Basin' },
    { type: SymbolType.SHOWER, label: 'Shower' },
    { type: SymbolType.BATH, label: 'Bath' },
    { type: SymbolType.SINK, label: 'Sink' },
    { type: SymbolType.WASHING_MACHINE, label: 'WM' },
    { type: SymbolType.DISHWASHER, label: 'DW' },
    { type: SymbolType.LAUNDRY_TUB, label: 'Tub' },
    { type: SymbolType.FLOOR_WASTE, label: 'FW' },
    { type: SymbolType.TUNDISH, label: 'Tundish' },
  ];

  const fittingSymbols = [
    { type: SymbolType.IO, label: 'IO' },
    { type: SymbolType.IOS, label: 'IOS' },
    { type: SymbolType.CO, label: 'CO' },
    { type: SymbolType.COS, label: 'COS' },
    { type: SymbolType.ORG, label: 'ORG' },
    { type: SymbolType.SWIVEL_JOINT, label: 'SJ' },
    { type: SymbolType.EXPANSION_JOINT, label: 'EJ' },
  ];

  const directionSymbols = [
    { type: SymbolType.RISER, label: 'Rise' },
    { type: SymbolType.DROPPER, label: 'Drop' },
  ];

  const systemOptions = [
    { type: SystemType.DRAINAGE, label: 'Drainage' },
    { type: SystemType.TRADE_WASTE, label: 'Trade' },
    { type: SystemType.VENT, label: 'Vent' },
    { type: SystemType.AFFLUENT, label: 'Affluent' },
  ];

  const getCategorySymbols = () => {
    switch (activeCategory) {
      case 'fittings': return fittingSymbols;
      case 'direction': return directionSymbols;
      default: return fixtureSymbols;
    }
  };

  const currentSymbols = getCategorySymbols();

  const blockPointer = (e: React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="absolute bottom-6 left-0 right-0 flex flex-col items-center pointer-events-none z-20 gap-3"
      onPointerDown={blockPointer}
    >
      {/* System Selector */}
      <div className="flex gap-1 p-1 glass-panel rounded-full pointer-events-auto">
        {systemOptions.map(sys => (
          <button
            key={sys.type}
            onClick={() => setActiveSystem(sys.type)}
            className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${activeSystem === sys.type
              ? 'text-white shadow-lg scale-105'
              : 'text-slate-500 hover:text-slate-300'
              }`}
            style={{
              backgroundColor: activeSystem === sys.type ? SYSTEM_COLORS[sys.type] : 'transparent'
            }}
          >
            {sys.label}
          </button>
        ))}
      </div>

      {/* Category Selection Menu */}
      {activeTool === ToolType.SYMBOL && isMenuOpen && (
        <div className="absolute bottom-[165px] left-4 pointer-events-auto animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="flex flex-col glass-panel rounded-2xl p-1.5 min-w-[140px]">
            <button
              onClick={() => { setActiveCategory('fixtures'); setIsMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeCategory === 'fixtures' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <Bath size={16} /> Fixtures
            </button>
            <button
              onClick={() => { setActiveCategory('fittings'); setIsMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeCategory === 'fittings' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <Box size={16} /> Fittings
            </button>
            <button
              onClick={() => { setActiveCategory('direction'); setIsMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeCategory === 'direction' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <Compass size={16} /> Direction
            </button>
          </div>
          <div className="w-4 h-4 bg-slate-900 border-r border-b border-white/20 rotate-45 absolute -bottom-2 left-6 -z-10" />
        </div>
      )}

      {/* Symbol Selection Strip & Category Trigger */}
      {activeTool === ToolType.SYMBOL && (
        <div className="flex items-end gap-2 px-4 w-full max-w-lg pointer-events-none">
          {/* Menu Trigger Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`pointer-events-auto h-16 w-16 shrink-0 flex flex-col items-center justify-center gap-1 glass-panel rounded-2xl transition-all active:scale-95 btn-premium ${isMenuOpen ? 'ring-2 ring-white/50 bg-slate-800' : ''}`}
          >
            {activeCategory === 'fixtures' && <Bath size={20} className="text-blue-400" />}
            {activeCategory === 'fittings' && <Box size={20} className="text-orange-400" />}
            {activeCategory === 'direction' && <Compass size={20} className="text-emerald-400" />}
            <span className="text-[8px] font-black text-white/50 uppercase tracking-tighter">Menu</span>
          </button>

          {/* Horizontal Symbol Strip */}
          <div
            className="flex-1 glass-panel rounded-2xl pointer-events-auto overflow-hidden h-16"
            onPointerDown={blockPointer}
          >
            <div className="flex items-center h-full overflow-x-auto no-scrollbar px-1 gap-1">
              {currentSymbols.map(s => (
                <button
                  key={s.type}
                  onClick={(e) => { e.stopPropagation(); setActiveSymbol(s.type); }}
                  className={`flex flex-col items-center justify-center gap-1 min-w-[54px] h-[54px] rounded-xl transition-all ${activeSymbol === s.type
                    ? 'bg-white/10 text-white shadow-inner scale-105'
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                    }`}
                >
                  <TradeIcon type={s.type} system={activeSystem} size={26} />
                  <span className={`text-[7px] font-black uppercase tracking-tighter text-center leading-tight ${activeSymbol === s.type ? 'text-white' : 'text-slate-500'}`}>
                    {s.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Mode Toolbar */}
      <div
        className="flex gap-1 p-1.5 glass-panel rounded-2xl w-[calc(100%-32px)] max-w-md pointer-events-auto"
        onPointerDown={blockPointer}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setActiveTool(ToolType.PAN); setIsMenuOpen(false); }}
          className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded-xl transition-all btn-premium ${activeTool === ToolType.PAN
            ? 'bg-slate-700 text-white shadow-inner'
            : 'text-slate-400 hover:text-white'
            }`}
        >
          <Move size={18} />
          <span className="font-bold text-[9px] uppercase tracking-widest">Navigate</span>
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); setActiveTool(ToolType.PIPES); setIsMenuOpen(false); }}
          className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded-xl transition-all btn-premium ${activeTool === ToolType.PIPES
            ? 'bg-blue-600 text-white shadow-lg'
            : 'text-slate-400 hover:text-white'
            }`}
        >
          <PencilLine size={18} />
          <span className="font-bold text-[9px] uppercase tracking-widest">Pipework</span>
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); setActiveTool(ToolType.SYMBOL); }}
          className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded-xl transition-all btn-premium ${activeTool === ToolType.SYMBOL
            ? 'bg-green-600 text-white shadow-lg'
            : 'text-slate-400 hover:text-white'
            }`}
        >
          <Bath size={18} />
          <span className="font-bold text-[9px] uppercase tracking-widest">Symbols</span>
        </button>

        <button
          data-testid="crop-tool-button"
          onClick={(e) => { e.stopPropagation(); setActiveTool(ToolType.CROP); setIsMenuOpen(false); }}
          className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded-xl transition-all btn-premium ${activeTool === ToolType.CROP
            ? 'bg-orange-600 text-white shadow-lg'
            : 'text-slate-400 hover:text-white'
            }`}
        >
          <Crop size={18} />
          <span className="font-bold text-[9px] uppercase tracking-widest">Crop</span>
        </button>
      </div>
    </div>
  );
};

export default Toolbar;